import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TabletLayout from "./TabletLayout";
import mesApi from "../../../Api/mes.api";
import { TimelineBatch } from "../../../types/mes";
import { toast, ToastContainer } from "react-toastify";
import { formatClockTime } from "../../../utils/processParameterSerialize";

/**
 * Tablet Oven View - Shows batches in baking phase
 */
const TabletOven = () => {
  const { oven } = useParams<{ oven?: string }>();
  const ovenName = oven || "Oven";

  const [batches, setBatches] = useState<TimelineBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadBatches();
    const refreshInterval = setInterval(loadBatches, 30000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
    };
  }, [ovenName]);

  const loadBatches = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      // Production-day view: everything physically baked today, incl. inbound
      // semi-finished made today for other days' plans.
      const { batches: dayBatches } = await mesApi.getProductionDay(today);

      if (dayBatches.length) {
        const ovenBatches = dayBatches
          .filter((b: TimelineBatch) => b.OvenName === ovenName || !oven)
          .sort((a: TimelineBatch, b: TimelineBatch) => {
            const aTime = a.BakingStart ? new Date(a.BakingStart).getTime() : 0;
            const bTime = b.BakingStart ? new Date(b.BakingStart).getTime() : 0;
            return aTime - bTime;
          });

        setBatches(ovenBatches);
      } else {
        setBatches([]);
      }
    } catch (error: any) {
      toast.error(`Greška: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      planned: { label: "PLANIRANO", color: "#6c757d" },
      in_progress: { label: "U PEĆNICI", color: "#e67e22" },
      completed: { label: "GOTOVO", color: "#007bff" },
    };
    const statusInfo = statusMap[status] || { label: status.toUpperCase(), color: "#6c757d" };
    return <span className="status-badge" style={{ background: statusInfo.color }}>{statusInfo.label}</span>;
  };

  const isCurrentBatch = (batch: TimelineBatch): boolean => {
    if (!batch.BakingStart || !batch.BakingEnd) return false;
    const now = currentTime.getTime();
    const start = new Date(batch.BakingStart).getTime();
    const end = new Date(batch.BakingEnd).getTime();
    return now >= start && now <= end;
  };

  return (
    <TabletLayout station={ovenName}>
      <ToastContainer position="top-center" autoClose={3000} />
      {loading ? (
        <div className="loading-state"><p>Učitavanje...</p></div>
      ) : batches.length === 0 ? (
        <div className="empty-state"><p>Nema planiranog pečenja</p></div>
      ) : (
        <div className="batches-list">
          {batches.map((batch) => {
            const isCurrent = isCurrentBatch(batch);
            return (
              <div key={batch.ID} className={`batch-card ${isCurrent ? "current-batch" : ""}`}>
                <div className="batch-header">
                  <div className="batch-time">{formatTime(batch.BakingStart)}</div>
                  {getStatusBadge(batch.BatchStatus)}
                </div>
                <div className="batch-id">{batch.MixCode}</div>
                <div className="batch-details">
                  <div className="detail-item">
                    <span className="detail-label">Proizvod:</span>
                    <span className="detail-value">{batch.ProductName || "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Masa:</span>
                    <span className="detail-value">{batch.FinalMassKg?.toFixed(1)} kg</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Trajanje:</span>
                    <span className="detail-value">~{batch.BakingTimeMin} min</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Završetak:</span>
                    <span className="detail-value">{formatTime(batch.BakingEnd)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
        .batches-list {display: flex; flex-direction: column; gap: 2rem;}
        .batch-card {background: #2a2f3e; border-radius: 12px; padding: 2rem; border-left: 6px solid #6c757d; box-shadow: 0 4px 12px rgba(0,0,0,0.3); transition: all 0.3s;}
        .batch-card.current-batch {border-left-color: #e67e22; background: #3d2e2f; box-shadow: 0 6px 20px rgba(230,126,34,0.3); animation: pulse 2s ease-in-out infinite;}
        @keyframes pulse {0%, 100% {box-shadow: 0 6px 20px rgba(230,126,34,0.3);} 50% {box-shadow: 0 8px 30px rgba(230,126,34,0.5);}}
        .batch-header {display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;}
        .batch-time {font-size: 3rem; font-weight: 700; color: #e67e22; font-family: 'Courier New', monospace;}
        .status-badge {padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 1.4rem; font-weight: 700; color: white; letter-spacing: 0.5px;}
        .batch-id {font-size: 2rem; font-weight: 600; color: #ffffff; margin-bottom: 1.5rem; word-break: break-word;}
        .batch-details {display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem;}
        .detail-item {display: flex; flex-direction: column; gap: 0.5rem;}
        .detail-label {font-size: 1.1rem; color: #adb3bd; text-transform: uppercase; letter-spacing: 0.5px;}
        .detail-value {font-size: 1.8rem; font-weight: 600; color: #ffffff;}
        .loading-state, .empty-state {display: flex; justify-content: center; align-items: center; height: 60vh; font-size: 2rem; color: #adb3bd;}
      `}</style>
    </TabletLayout>
  );
};

export default TabletOven;
